/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildHomeLocationPayload, getCountryOptions, useAddressValidationFlow } from '@homeexchange/home';
import { Callout } from '@homeexchange/panorama';
import { buildAddressText } from '../../../utils/buildAddressText';

import SignupCollectionAction from '../SignupCollectionAction';
import HomeAddressCollection from '../HomeAddressCollection';
import Loading from '../../common/Loading';
import { setFormData } from '../../../actions/collection';
import HomeApi from '../../../api/Home';
import i18n from '../../../i18n';
import withAnalytics from '../../analytics/withAnalytics';
import CollectionFunnelEvents from '../../../utils/collectionFunnelEvents';

const Step3CollectionForm = (props) => {
    const { goToStep } = props;
    const { state: routerState } = useLocation();
    const [isSending, setIsSending] = useState(false);
    const [hasSubmitError, setHasSubmitError] = useState(false);

    const locale = i18n.language;
    const sessionToken = useMemo(() => crypto.randomUUID(), []);
    const countryOptions = useMemo(() => getCountryOptions(locale), [locale]);
    const wasManualRef = useRef(false);

    const config = useMemo(
        () => ({
            theme: 'collection',
            locale,
            googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY,
            googleMapsMapId: process.env.GOOGLE_MAPS_MAP_ID,
            sessionToken
        }),
        [locale, sessionToken]
    );

    const initialValue = useMemo(() => {
        const address = props.formCollectionData?.address;
        if (!address) {
            return undefined;
        }
        return {
            address,
            location: props.formCollectionData?.location
        };
    }, [props.formCollectionData]);

    const handleAccepted = async (value) => {
        setHasSubmitError(false);
        setIsSending(true);
        const address = buildAddressText(value, countryOptions) || value.address;
        const payload = {
            ...props.formCollectionData,
            address,
            location: buildHomeLocationPayload(value, address),
            location_id: value.location_id,
            overridden_address: wasManualRef.current ? 1 : 0
        };
        delete payload.provider;

        try {
            await HomeApi.addHomeCollection(payload);
            props.setFormData(payload);

            CollectionFunnelEvents.funnelProgress(3);
            props.track('HEC Home init', { category: 'HEC application' });
            goToStep('step4');
        } catch (error) {
            setIsSending(false);
            setHasSubmitError(true);
        }
    };

    const addressFlow = useAddressValidationFlow({
        onAccepted: handleAccepted,
        initialValue,
        initialStep: initialValue?.location && routerState?.fromStep > 3 ? 'pin' : undefined,
        locale
    });

    useEffect(() => {
        if (addressFlow.step === 'manual') {
            wasManualRef.current = true;
        }
    }, [addressFlow.step]);

    const handleBackOverride = () => {
        if (addressFlow.step === 'pin' && !addressFlow.value.location_id) {
            addressFlow.reset();
            return true;
        }
        if (addressFlow.step === 'confirm' || addressFlow.step === 'manual' || addressFlow.step === 'pin') {
            addressFlow.onBack();
            return true;
        }
        return false;
    };

    const hasSavedLocation =
        addressFlow.step === 'address' &&
        !addressFlow.value.location_id &&
        !!addressFlow.value.location &&
        !!addressFlow.value.address &&
        addressFlow.value.address === initialValue?.address;

    const handleConfirm = () => {
        if (hasSavedLocation) {
            handleAccepted(addressFlow.value);
            return;
        }
        addressFlow.onConfirm();
    };

    const isAddressActionEnabled =
        hasSavedLocation ||
        (!addressFlow.isConfirmDisabled && (addressFlow.step === 'manual' || !!addressFlow.value?.address));

    return (
        <>
            <div className="step3-container">
                <div className="step-circle">
                    <span>3</span>
                </div>
                <HomeAddressCollection
                    addressFlow={addressFlow}
                    config={config}
                    countryOptions={countryOptions}
                    onConfirm={handleConfirm}
                />
                {hasSubmitError && <Callout hierarchy="error">{i18n.t('common:error-retry')}</Callout>}
            </div>
            <SignupCollectionAction
                step={3}
                nextLabel={i18n.t(
                    addressFlow.step === 'address' ? 'global:global.continue' : 'global:global.confirm'
                )}
                isValid={isAddressActionEnabled}
                isSending={isSending || addressFlow.isValidating}
                onBackOverride={handleBackOverride}
            />
        </>
    );
};

Step3CollectionForm.propTypes = {
    formCollectionData: PropTypes.object,
    setFormData: PropTypes.func,
    track: PropTypes.func,
    goToStep: PropTypes.func
};

export const Step3Collection = (props) => {
    const navigate = useNavigate();
    const goToStep = (step) => navigate(location.pathname.replace(/[^/]*$/, step));
    const isLoaded = !_.isEmpty(props.formCollectionData);

    useEffect(() => {
        if (isLoaded) {
            return;
        }
        HomeApi.getHomeCollection()
            .then((response) => (response?.type ? props.setFormData(response) : goToStep('step2')))
            .catch(() => goToStep('step2'));
    }, []);

    if (!isLoaded) {
        return (
            <div className="step3-container">
                <Loading />
            </div>
        );
    }
    return <Step3CollectionForm {...props} goToStep={goToStep} />;
};

Step3Collection.propTypes = {
    formCollectionData: PropTypes.object,
    setFormData: PropTypes.func,
    track: PropTypes.func
};

const mapDispatchToProps = (dispatch) => ({
    setFormData: bindActionCreators(setFormData, dispatch)
});

const mapStateToProps = (state) => ({
    formCollectionData: state.collection.formCollection
});

export default compose(connect(mapStateToProps, mapDispatchToProps), withAnalytics)(Step3Collection);
